<template>
  <div class="operator-card">
    <div class="operator-card-info">
      <v-avatar color="primary">{{ initials }}</v-avatar>
      <div class="identifiers">
        <div class="name">
          {{ operator.name }}
        </div>
        <div class="login">
          {{ operator.login }}
        </div>
      </div>

      <div class="status">
        Status:
        <v-select
          class="status-select"
          v-model="status"
          :items="statusOptions"
          :disabled="readonly"
          :color="statusToColor[operator.status]"
          label="Status"
          outlined
          solo
          dense
          flat
          hide-details
          @change="$emit('update-status', $event)"
        />
      </div>

      <div class="projects">
        <template v-if="!isEditingProjects">
          Projects:
          <v-chip
            v-for="entityId in operator.entities?.slice(0, MAX_ENTITIES_DISPLAY)"
            v-if="entitiesById[entityId]"
            :key="entityId"
            :color="operator.base === entityId ? 'primary': ''"
            :close="!readonly"
            @click:close="onDeleteOneProject(entityId)"
          >{{ entitiesById[entityId].text }}</v-chip>
          <v-chip
            v-if="operator.entities?.length > MAX_ENTITIES_DISPLAY"
            class="more"
            outlined
          >
            +{{ operator.entities.length - MAX_ENTITIES_DISPLAY }} more
          </v-chip>
          <v-icon
            :disabled="readonly"
            @click="onEditProjectsInline"
          >
            add
          </v-icon>
        </template>
        <v-select
          v-else
          :value="operator.entities"
          :items="Object.keys(entitiesById).map(Number)"
          multiple
          dense
          hide-details
          @input="localEntityValue = $event"
          @blur="onBlur"
        >
          <template #prepend>
            Projects:
          </template>
          <template #item="{ item }">
            {{ entitiesById[item].text }}
          </template>

          <template #selection="{ item, index }">
            <v-chip v-if="index < MAX_ENTITIES_DISPLAY">
              {{ entitiesById[item].text }}
            </v-chip>
            <template v-if="index === MAX_ENTITIES_DISPLAY">
              <v-chip
                class="more"
                outlined
              >
               +{{ operator.entities.length - MAX_ENTITIES_DISPLAY }} more
              </v-chip>
            </template>
          </template>

          <v-icon slot="append" @click="isEditingProjects = false">
            close
          </v-icon>
        </v-select>
      </div>
    </div>
    <div class="operator-card-permissions">
      <div class="title-block">
        <div class="title">
          <v-icon
            large
            @click="isPermissionsExpanded = !isPermissionsExpanded"
            style="font-size: 1.5rem"
          >
            {{ permissionExpansionIcon }}
          </v-icon>
          <span class="title-text">
            Role Permissions
            <v-chip
              class="permission-count"
              v-if="!isPermissionsExpanded"
              color="primary"
              small
            >{{ enabledPermissionCount }}</v-chip>
          </span>
        </div>
        <div class="actions">
          <v-btn
            v-if="canViewUserHistory"
            icon
            @click="$emit('history')"
          >
            <v-icon>
              history
            </v-icon>
          </v-btn>
          <v-btn
            icon
            :disabled="readonly"
            @click="$emit('edit')"
          >
            <v-icon>
              edit
            </v-icon>
          </v-btn>
        </div>
      </div>

      <v-expand-transition>
        <operator-permissions
          v-if="isPermissionsExpanded"
          :operator="operator"
          :permissions-by-group="permissionsByGroup"
          :readonly="readonly"
          @update-permissions="$emit('update-permissions', $event)"
        />
      </v-expand-transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .operator-card {
    background-color: #fff;
    border-radius: 0.25rem;
    box-shadow: 0px 1px 3px 0px #0000001F,
    0px 1px 1px 0px #00000024,
    0px 2px 1px -1px #00000033;

    &:not(:last-child) {
      margin-bottom: 1rem;
    }

    &-info {
      padding: 1rem;
    }

    &-info {
      display: flex;
      width: 100%;
      border-bottom: 1px solid #E0E0E0;
      padding: 1rem;
      align-items: flex-start;

      .v-avatar {
        color: #fff;
        font-weight: bold;
        margin-right: 0.75rem;
      }

      .identifiers {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        .name {
          font-size: 1.25rem;
          font-weight: 500;
          margin-right: 3.75rem;
          width: 10.25rem;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
        .login {
          font-size: 0.875rem;
          color: #000;
        }
      }

      .status {
        display: flex;
        align-items: center;
        margin-left: auto;
        margin-right: 1rem;
        align-self: flex-start;
        gap: 0.5rem;
        max-width: 180px;
      }

      .projects {
        line-height: 40px;
        align-self: flex-start;
        color: #000;

        :before {
          margin-bottom: -5px;
        }

        .v-chip {
          margin: 0 0.5rem;
          color: #fff;
          background-color: #007dff;

          &.more {
            color: #007dff;
            border-color: #007dff;
            margin-left: auto;
            order: 2;
          }
        }
      }
    }

    &-permissions {
      padding: 0.6875rem 1rem;

      .title-block {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-right: 1rem;

        .title {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          &-text {
            font-size: 1rem;
            font-weight: 400;
            color: #000;

            .permission-count {
              margin-left: 0.75rem;
            }
          }
        }

        .actions {
          .v-icon {
            background-color: #4caf50;
            border-radius: 50%;
            color: #fff;
          }
        }
      }

      .operator-permissions {
        margin-top: 1rem;
      }
    }
  }
</style>

<script>
import OperatorPermissions from './OperatorPermissions.vue';

const MAX_ENTITIES_DISPLAY = 5;

export default {
  components: {
    OperatorPermissions
  },
  props: {
    operator: {
      type: Object,
      default: () => ({})
    },
    entitiesById: {
      type: Object,
      default: () => ({})
    },
    permissionsByGroup: {
      type: Object,
      default: () => ({})
    },
    readonly: Boolean,
    canViewUserHistory: Boolean,
  },
  data() {
    return {
      localEntityValue: [...(this.operator.entities || [])],
      isPermissionsExpanded: false,
      isEditingProjects: false,
      MAX_ENTITIES_DISPLAY,
      status: this.operator.status,
      statusOptions: [ 'active', 'blocked', 'frozen' ],
      statusToColor: {
        'active': '#4caf50',
        'blocked': '#f55a4e',
        'frozen': '#ffa21a'
      }
    }
  },
  emits: ['update-permissions', 'update-projects', 'update-status', 'edit'],
  computed: {
    initials() {
      return this.operator.name
        ? this.operator.name.trim().split(' ').slice(0, 2).map(namePart => namePart[0].toUpperCase()).join('')
        : this.operator.login.slice(0, 2).toUpperCase();
    },
    enabledPermissionCount() {
      return Object.keys(this.operator.permission).reduce((acc, currentKey) => {
        const permissionValue = this.operator.permission[currentKey];
        switch(typeof permissionValue) {
          case 'object':
            acc += !!permissionValue.length;
            break;
          default:
            acc += !!permissionValue;
            break;
        }

        return acc;
      }, 0);
    },
    permissionExpansionIcon() {
      return this.isPermissionsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down';
    },
  },
  methods: {
    onBlur() {
      const arraysAreIdentical = this.localEntityValue.length === this.operator.entities.length
                                  && this.localEntityValue.every(value => this.operator.entities.includes(value));
      if (arraysAreIdentical) {
        this.isEditingProjects = false;
        return;
      }

      this.$emit('update-projects', this.localEntityValue);
    },

    onEditProjectsInline() {
      this.localEntityValue = [...this.operator.entities];
      this.isEditingProjects = true;
    },

    onDeleteOneProject(projectId) {
      this.$emit('update-projects', this.operator.entities.filter(id => id !== projectId));
    }
  },

  watch: {
    readonly(newValue, oldValue) {
      if (!newValue && oldValue) {
        this.isEditingProjects = false;
        this.status = this.operator.status;
      }
    }
  }
}
</script>
